import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Lightning, Envelope, Lock, User, GoogleLogo, GithubLogo, Sparkle } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

export default function Auth() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "signup" ? "signup" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [refInfo, setRefInfo] = useState(null);
  const { login, register } = useAuth();
  const nav = useNavigate();

  // Capture ?ref=CODE on mount + persist
  React.useEffect(() => {
    const codeFromUrl = params.get("ref") || localStorage.getItem("cf_ref");
    if (codeFromUrl) {
      localStorage.setItem("cf_ref", codeFromUrl.toUpperCase());
      api.post("/referrals/resolve", { code: codeFromUrl.toUpperCase() })
        .then(r => r.data.valid && setRefInfo(r.data))
        .catch(() => {});
    }
    // eslint-disable-next-line
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success("Welcome back!");
      } else {
        const refCode = localStorage.getItem("cf_ref");
        await register(name, email, password, refCode);
        localStorage.removeItem("cf_ref");
        toast.success("Account created — let's forge.");
      }
      nav("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 relative overflow-hidden" data-testid="auth-page">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-[#FF3B6B]/20 blur-[120px] rounded-full" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-[#00E5FF]/10 blur-[100px] rounded-full" />
      </div>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="relative w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8" data-testid="auth-logo">
          <div className="w-9 h-9 rounded-md bg-[#FF3B6B] flex items-center justify-center shadow-[0_0_24px_-4px_#FF3B6B]">
            <Lightning weight="fill" className="text-white" size={20} />
          </div>
          <span className="font-semibold text-xl">ClipForge<span className="text-[#FF3B6B]">AI</span></span>
        </Link>
        <div className="glass-strong rounded-2xl p-8">
          {refInfo && (
            <div className="mb-5 flex items-center gap-3 bg-[#FF3B6B]/10 border border-[#FF3B6B]/30 rounded-lg p-3" data-testid="ref-banner">
              <Sparkle weight="fill" className="text-[#FF3B6B] shrink-0" />
              <p className="text-sm"><span className="text-[#FF3B6B] font-semibold">{refInfo.referrer_name}</span> invited you to ClipForge AI</p>
            </div>
          )}
          <h1 className="text-2xl font-bold tracking-tighter">{mode === "login" ? "Welcome back" : "Create your account"}</h1>
          <p className="text-sm text-white/50 mt-1">{mode === "login" ? "Sign in to your studio" : "50 free AI credits to start"}</p>

          <button onClick={googleLogin} className="mt-6 w-full bg-white text-black py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 hover:bg-white/90 transition-colors" data-testid="auth-google-btn">
            <GoogleLogo weight="bold" size={18} /> Continue with Google
          </button>

          <button
            onClick={() => {
              const ref = localStorage.getItem("cf_ref");
              const qs = ref ? `?ref=${encodeURIComponent(ref)}` : "";
              window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/github/login${qs}`;
            }}
            className="mt-3 w-full bg-[#24292e] hover:bg-[#1b1f23] border border-white/10 text-white py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors"
            data-testid="auth-github-btn"
          >
            <GithubLogo weight="fill" size={18} /> Continue with GitHub
          </button>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-xs text-white/40 font-mono">OR</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "signup" && (
              <div>
                <label className="text-xs text-white/60 mb-1.5 block">Full name</label>
                <div className="relative">
                  <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                  <input value={name} onChange={(e) => setName(e.target.value)} required className="w-full bg-white/5 border border-white/10 rounded-lg px-10 py-2.5 text-sm focus:border-[#FF3B6B] focus:outline-none transition-colors" data-testid="auth-name-input" />
                </div>
              </div>
            )}
            <div>
              <label className="text-xs text-white/60 mb-1.5 block">Email</label>
              <div className="relative">
                <Envelope size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full bg-white/5 border border-white/10 rounded-lg px-10 py-2.5 text-sm focus:border-[#FF3B6B] focus:outline-none transition-colors" data-testid="auth-email-input" />
              </div>
            </div>
            <div>
              <label className="text-xs text-white/60 mb-1.5 block">Password</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="w-full bg-white/5 border border-white/10 rounded-lg px-10 py-2.5 text-sm focus:border-[#FF3B6B] focus:outline-none transition-colors" data-testid="auth-password-input" />
              </div>
            </div>
            <button disabled={loading} type="submit" className="w-full btn-primary py-3 rounded-lg font-semibold disabled:opacity-50" data-testid="auth-submit-btn">
              {loading ? "Loading..." : mode === "login" ? "Sign in" : "Create account"}
            </button>
            {mode === "login" && (
              <div className="text-center">
                <Link to="/forgot-password" className="text-xs text-white/50 hover:text-[#FF3B6B] transition-colors" data-testid="auth-forgot-link">
                  Forgot your password?
                </Link>
              </div>
            )}
          </form>

          <p className="text-center text-sm text-white/50 mt-6">
            {mode === "login" ? "New to ClipForge?" : "Already have an account?"}{" "}
            <button onClick={() => setMode(mode === "login" ? "signup" : "login")} className="text-[#FF3B6B] hover:underline" data-testid="auth-mode-toggle">
              {mode === "login" ? "Create account" : "Sign in"}
            </button>
          </p>
        </div>
      </motion.div>
    </div>
  );
}
